import React, { useEffect, useRef, useState } from "react";
import {
  Answers,
  Appointment,
  Step,
  DRAFT_KEY,
  emptyAnswers,
  emptyAppointment,
  intentions,
  capitals,
  zones,
  hours,
  previous,
  restoreDraft,
  contactErrors,
  dateLabel,
  validAppointment,
  buildLead,
  attribution,
  demoTransport,
  LeadTransport,
  demoAgenda,
  AgendaAdapter,
} from "./model";
import LiveContactAgenda from "./LiveContactAgenda";
const headings: Record<Step, string> = {
  intro: "Una franquicia REMAX es un negocio propio.",
  p1: "¿Qué estás buscando en REMAX?",
  p2: "¿Con qué capital cuentas para la apertura de tu oficina?",
  p3: "¿En qué zona te gustaría abrir tu oficina?",
  p4: "¿A quién contactamos?",
  p5: "Elige el día y la hora de tu reunión.",
  ok: "Demostración completada.",
  pa: "Buscas algo distinto, y también lo tenemos.",
  pb: "Gracias por tu interés.",
};
const contactLabels = {
  nombre: [
    "Nombre completo",
    "Nombre y apellido",
    "Escribe tu nombre y apellido.",
  ],
  telefono: [
    "Teléfono",
    "809 000 0000",
    "Escribe un teléfono válido de al menos 10 dígitos.",
  ],
  email: [
    "Correo electrónico",
    "nombre@correo.com",
    "Escribe un correo electrónico válido.",
  ],
};
const meetingItems = [
  "El modelo de franquicia completo y cómo opera una oficina REMAX",
  "Qué incluye el respaldo: marca, tecnología, capacitación, red y acompañamiento",
  "Las condiciones de inversión con el desglose real, sin estimados",
  "La disponibilidad de territorio en la zona que seleccionaste",
];
export default function FranchiseForm({
  live = false,
  transport = demoTransport,
  agenda = demoAgenda,
}: {
  live?: boolean;
  transport?: LeadTransport;
  agenda?: AgendaAdapter;
}) {
  const [step, setStep] = useState<Step>("intro"),
    [answers, setAnswers] = useState<Answers>({ ...emptyAnswers }),
    [appointment, setAppointment] = useState<Appointment>({
      ...emptyAppointment,
    });
  const [ready, setReady] = useState(false),
    [back, setBack] = useState(false),
    [days, setDays] = useState<string[]>([]),
    [errors, setErrors] = useState<Partial<Record<keyof Answers, boolean>>>({});
  const [announcement, setAnnouncement] = useState(""),
    [status, setStatus] = useState<
      "idle" | "sending" | "error" | "demo-complete" | "accepted"
    >("idle"),
    [failure, setFailure] = useState("");
  const [recruitment, setRecruitment] = useState(
    "https://reclutamiento.remaxrd.com/?utm_source=landing_franquicias&utm_medium=landing&utm_id=campaign_franquicias",
  );
  const panel = useRef<HTMLElement>(null),
    form = useRef<HTMLFormElement>(null),
    timer = useRef<ReturnType<typeof setTimeout> | null>(null),
    pending = useRef(false),
    submitting = useRef(false),
    mounted = useRef(false),
    request = useRef(""),
    focusNext = useRef(false);
  const cancel = () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    pending.current = false;
  };
  useEffect(() => {
    mounted.current = true;
    const dates = agenda.days();
    setDays(dates);
    try {
      const draft = restoreDraft(sessionStorage.getItem(live ? "franquicias-live-choices-v1" : DRAFT_KEY));
      if (draft) {
        setAnswers(draft.answers);
        setStep(live && ["p5", "ok"].includes(draft.step) ? "p4" : draft.step);
        setAppointment(
          draft.appointment.fecha
            ? draft.appointment
            : { fecha: dates[0], hora: "" },
        );
      } else setAppointment({ fecha: dates[0], hora: "" });
    } catch {
      setAppointment({ fecha: dates[0], hora: "" });
    }
    const url = new URL(recruitment);
    Object.entries(attribution(location.href)).forEach(([k, v]) =>
      url.searchParams.set("src_" + k, v),
    );
    setRecruitment(url.toString());
    request.current =
      typeof crypto.randomUUID === "function"
        ? crypto.randomUUID()
        : Date.now().toString(36) + "-" + Math.random().toString(36).slice(2);
    setReady(true);
    return () => {
      mounted.current = false;
      cancel();
    };
    // The adapters are fixed for the lifetime of this form.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (!ready || step === "ok") return;
    try {
      sessionStorage.setItem(
        live ? "franquicias-live-choices-v1" : DRAFT_KEY,
        JSON.stringify({ version: 1, step, answers, appointment }),
      );
    } catch {
      /* Draft recovery is optional. */
    }
  }, [ready, step, answers, appointment]);
  useEffect(() => {
    if (!focusNext.current) return;
    focusNext.current = false;
    panel.current?.focus({ preventScroll: true });
    const box = panel.current?.getBoundingClientRect();
    if (box && (box.top < 90 || box.top > innerHeight * 0.55))
      panel.current?.scrollIntoView({
        block: "start",
        behavior: matchMedia("(prefers-reduced-motion: reduce)").matches
          ? "auto"
          : "smooth",
      });
  }, [step]);
  const go = (next: Step, isBack = false) => {
    cancel();
    focusNext.current = true;
    setBack(isBack);
    setStep(next);
    setAnnouncement(headings[next]);
    setFailure("");
  };
  const choose = (name: "intencion" | "capital" | "zona", value: string) => {
    if (pending.current || submitting.current) return;
    setAnswers((a) => ({
      ...a,
      [name]: value,
      ...(name === "intencion" && a.intencion !== value
        ? { capital: "", zona: "" }
        : {}),
    }));
    const next: Step =
      name === "intencion"
        ? value === intentions[2]
          ? "pa"
          : value === intentions[3]
            ? "pb"
            : "p2"
        : name === "capital"
          ? "p3"
          : "p4";
    pending.current = true;
    timer.current = setTimeout(() => go(next), 190);
  };
  const checkContact = () => {
    const e = contactErrors(answers);
    setErrors(e);
    if (Object.values(e).some(Boolean)) {
      setAnnouncement("Revisa los campos marcados.");
      const first = (Object.keys(e) as (keyof typeof e)[]).find((k) => e[k]);
      form.current?.querySelector<HTMLInputElement>("#f-" + first)?.focus();
      return false;
    }
    return true;
  };
  const confirm = async () => {
    if (submitting.current) return;
    if (!checkContact()) {
      go("p4");
      return;
    }
    if (!validAppointment(appointment, agenda.days())) {
      setAnnouncement("Elige un día y una hora disponibles para continuar.");
      setFailure("Elige un día y una hora disponibles para continuar.");
      return;
    }
    submitting.current = true;
    setStatus("sending");
    setFailure("");
    try {
      const lead = buildLead(
        answers,
        appointment,
        location.href,
        request.current,
      );
      const result = await transport.submit(lead);
      if (!mounted.current) return;
      if (
        result.requestId !== lead.requestId ||
        !["demo-complete", "accepted"].includes(result.status)
      )
        throw new Error("Respuesta no válida");
      setStatus(result.status);
      try {
        sessionStorage.removeItem(DRAFT_KEY);
      } catch {}
      go("ok");
    } catch {
      if (mounted.current) {
        setStatus("error");
        setFailure(
          "No se pudo completar la solicitud. Tus respuestas siguen aquí; vuelve a intentarlo.",
        );
      }
    } finally {
      submitting.current = false;
    }
  };
  const next = () => {
    if (step === "intro") go("p1");
    else if (step === "p4" && !live) {
      if (checkContact()) go("p5");
    } else if (step === "p5") void confirm();
  };
  const backButton = (
    <button
      type="button"
      className="back"
      disabled={status === "sending"}
      onClick={() => go(previous(step), true)}
    >
      {step === "pa"
        ? "Volver"
        : step === "pb"
          ? "Volver y cambiar mi respuesta"
          : "Atrás"}
    </button>
  );
  const group =
    step === "p1"
      ? { name: "intencion" as const, items: intentions }
      : step === "p2"
        ? { name: "capital" as const, items: capitals }
        : step === "p3"
          ? { name: "zona" as const, items: zones }
          : null;
  const progress = ["intro", "p1", "p2", "p3", "p4", "p5"].indexOf(step);
  return (
    <>
      <div className="prog" role="presentation">
        <i
          id="prog"
          style={{ width: (progress < 0 ? 100 : progress * 20) + "%" }}
        />
      </div>
      <form
        id="frq"
        className="card-body"
        noValidate
        autoComplete="on"
        ref={form}
        onSubmit={(e) => {
          e.preventDefault();
          next();
        }}
        aria-busy={status === "sending"}
      >
        {!live && (
          <p className="demo-note">
            Modo demostración: usa datos de prueba. No se enviarán datos ni se reservará una reunión.
          </p>
        )}
        <div className="sr" role="status" aria-live="polite">
          {announcement}
        </div>
        <section
          className={
            "fstep act" +
            (back ? " back" : "") +
            (step === "ok" || step === "pa" || step === "pb" ? " done" : "")
          }
          data-step={step}
          tabIndex={-1}
          ref={panel}
          key={step}
          aria-labelledby="form-heading"
        >
          <p className="fmeta">
            <span>
              {step === "intro" ? (
                "Solicitud de reunión"
              ) : step === "p5" ? (
                "Último paso"
              ) : group || step === "p4" ? (
                <>
                  Pregunta{" "}
                  <span className="qn">{step.slice(1).padStart(2, "0")}</span>{" "}
                  de 4
                </>
              ) : step === "pa" ? (
                "Otro camino"
              ) : step === "pb" ? (
                "Con calma"
              ) : (
                "Demostración"
              )}
            </span>
            <span>
              {step === "intro"
                ? "1 min"
                : step === "p5"
                  ? "Agenda"
                  : step === "p1"
                    ? "Intención"
                    : step === "p2"
                      ? "Inversión"
                      : step === "p3"
                        ? "Territorio"
                        : step === "p4"
                          ? "Contacto"
                          : ""}
            </span>
          </p>
          <h3 className="fq" id="form-heading">
            {step === "ok" && status === "accepted"
              ? "Recibimos tu solicitud."
              : headings[step]}
          </h3>
          {step === "intro" && (
            <>
              <p className="fhelp">
                Diriges tu oficina, formas tu equipo y construyes un activo que
                te pertenece. No es una vacante de agente inmobiliario. Responde
                cuatro preguntas y elige el día de tu reunión de presentación.
              </p>
              <div className="fnav">
                <button
                  type="submit"
                  disabled={!ready}
                  className="btn btn-1 btn-block"
                >
                  Empezar <Arrow />
                </button>
              </div>
              <p className="micro" style={{ marginTop: "var(--s4)" }}>
                La apertura de una franquicia requiere una inversión inicial.
              </p>
            </>
          )}
          {group && (
            <>
              {step === "p2" && (
                <p className="fhelp">
                  Ninguna respuesta te descarta. Nos sirve para preparar una
                  presentación ajustada a tu situación.
                </p>
              )}
              <div
                className="opts"
                role="radiogroup"
                aria-labelledby="form-heading"
              >
                {group.items.map((value) => (
                  <label className="opt" key={value}>
                    <input
                      type="radio"
                      name={group.name}
                      value={value}
                      checked={answers[group.name] === value}
                      onChange={() => {}}
                      onClick={() => choose(group.name, value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          choose(group.name, value);
                        }
                      }}
                    />
                    <span>{value}</span>
                  </label>
                ))}
              </div>
              <div className="fnav">{backButton}</div>
            </>
          )}
          {step === "p4" && live && <LiveContactAgenda answers={answers} onBack={() => go("p3", true)} />}
          {step === "p4" && !live && (
            <>
              <p className="fhelp">
                Un representante de REMAX República Dominicana te contactará
                para confirmar tu reunión de presentación.
              </p>
              <div className="fields">
                {(
                  Object.keys(contactLabels) as (keyof typeof contactLabels)[]
                ).map((name) => (
                  <div
                    className={"fld" + (errors[name] ? " err" : "")}
                    data-fld={name}
                    key={name}
                  >
                    <label htmlFor={"f-" + name}>
                      {contactLabels[name][0]}
                    </label>
                    <input
                      id={"f-" + name}
                      name={name}
                      type={
                        name === "email"
                          ? "email"
                          : name === "telefono"
                            ? "tel"
                            : "text"
                      }
                      inputMode={
                        name === "telefono"
                          ? "tel"
                          : name === "email"
                            ? "email"
                            : "text"
                      }
                      autoComplete={
                        name === "nombre"
                          ? "name"
                          : name === "telefono"
                            ? "tel"
                            : "email"
                      }
                      placeholder={contactLabels[name][1]}
                      value={answers[name]}
                      maxLength={name === "telefono" ? 40 : 254}
                      required
                      aria-invalid={!!errors[name]}
                      aria-describedby={errors[name] ? "e-" + name : undefined}
                      onChange={(e) => {
                        const updated = { ...answers, [name]: e.target.value };
                        setAnswers(updated);
                        if (errors[name])
                          setErrors((old) => ({
                            ...old,
                            [name]: contactErrors(updated)[name],
                          }));
                      }}
                      onBlur={() => {
                        if (errors[name])
                          setErrors((old) => ({
                            ...old,
                            [name]: contactErrors(answers)[name],
                          }));
                      }}
                    />
                    <span className="msg" id={"e-" + name}>
                      {contactLabels[name][2]}
                    </span>
                  </div>
                ))}
              </div>
              <label
                className={"consent" + (errors.consent ? " err" : "")}
                htmlFor="f-consent"
              >
                <input
                  id="f-consent"
                  name="consent"
                  type="checkbox"
                  required
                  checked={answers.consent}
                  aria-invalid={!!errors.consent}
                  aria-describedby={errors.consent ? "e-consent" : undefined}
                  onChange={(e) => {
                    setAnswers({ ...answers, consent: e.target.checked });
                    setErrors({ ...errors, consent: !e.target.checked });
                  }}
                />
                <span>
                  Sí, quiero que REMAX República Dominicana me contacte por
                  llamada, WhatsApp o correo para coordinar una reunión de
                  presentación sobre la franquicia.{" "}
                  <a
                    href="/politica-privacidad"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Política de privacidad
                  </a>
                  .
                </span>
              </label>
              {errors.consent && (
                <p className="submit-error" id="e-consent">
                  Debes autorizar el contacto para continuar.
                </p>
              )}
              <div className="fnav">
                <button type="submit" className="btn btn-1">
                  Elegir día y hora <Arrow />
                </button>
                {backButton}
              </div>
            </>
          )}
          {step === "p5" && (
            <>
              <p className="fhelp">
                Reunión de presentación de 45 minutos, presencial o por
                videollamada. Puedes reprogramarla si lo necesitas.
              </p>
              <div className="hs-slot" data-placeholder="true">
                <div className="cal-hd">
                  <span className="m">
                    {appointment.fecha
                      ? dateLabel(appointment.fecha, {
                          month: "long",
                          year: "numeric",
                        })
                      : ""}
                  </span>
                  <span className="micro">
                    Zona horaria: Santo Domingo (GMT-4)
                  </span>
                </div>
                <div className="days" role="group" aria-label="Elige un día">
                  {days.map((date) => (
                    <button
                      type="button"
                      className="day"
                      key={date}
                      disabled={status === "sending"}
                      aria-pressed={appointment.fecha === date}
                      aria-label={dateLabel(date)}
                      onClick={() => {
                        setAppointment({ fecha: date, hora: "" });
                        setFailure("");
                        setAnnouncement(
                          "Día seleccionado: " +
                            dateLabel(date) +
                            ". Elige una hora.",
                        );
                      }}
                    >
                      <span className="dw">
                        {dateLabel(date, { weekday: "short" })}
                      </span>
                      <span className="dn">{Number(date.slice(-2))}</span>
                    </button>
                  ))}
                </div>
                <p className="fmeta" style={{ marginBottom: "var(--s3)" }}>
                  <span>Horarios disponibles</span>
                </p>
                <div className="slots" role="group" aria-label="Elige una hora">
                  {hours.map((hour) => (
                    <button
                      type="button"
                      className="slot"
                      key={hour}
                      disabled={
                        status === "sending" ||
                        !appointment.fecha ||
                        !agenda.available(appointment.fecha, hour)
                      }
                      aria-pressed={appointment.hora === hour}
                      onClick={() => {
                        setAppointment({ ...appointment, hora: hour });
                        setFailure("");
                        setAnnouncement("Hora seleccionada: " + hour);
                      }}
                    >
                      {hour}
                    </button>
                  ))}
                </div>
                <p className="hs-note">
                  Vista previa del calendario. Los horarios son simulados y no
                  representan disponibilidad real.
                </p>
              </div>
              {failure && (
                <p className="submit-error" role="alert">
                  {failure}
                </p>
              )}
              <div className="fnav">
                <button
                  type="submit"
                  className="btn btn-1"
                  disabled={status === "sending"}
                >
                  {status === "sending"
                    ? "Completando…"
                    : "Confirmar mi reunión"}{" "}
                  <Arrow />
                </button>
                {backButton}
              </div>
            </>
          )}
          {step === "ok" && (
            <>
              <div className="tick">
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M4 12.6 9.2 18 20 6.6" strokeLinecap="square" />
                </svg>
              </div>
              <p className="fhelp">
                {status === "accepted"
                  ? "Tu solicitud fue recibida. La reunión queda pendiente de confirmación."
                  : "No se enviaron datos ni se reservó una reunión. Has completado el recorrido de prueba."}
              </p>
              <div className="appt">
                <span className="k">
                  {status === "accepted"
                    ? "Horario solicitado"
                    : "Horario de ejemplo"}
                </span>
                <span className="v">
                  {dateLabel(appointment.fecha)} · {appointment.hora}
                </span>
              </div>
              <p className="fmeta">
                <span>Qué vas a ver en la reunión</span>
              </p>
              <ul className="next">
                {meetingItems.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </>
          )}
          {step === "pa" && (
            <>
              <p className="fhelp">
                Lo que se solicita aquí es información sobre adquirir una
                franquicia, es decir, ser dueño de una oficina REMAX. Si lo que
                te interesa es desarrollarte como agente inmobiliario dentro de
                una oficina, ese es otro camino y también está abierto.
              </p>
              <div className="fnav">
                <a
                  className="btn btn-1"
                  href={recruitment}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Ir al proceso para agentes <Arrow />
                </a>
                {backButton}
              </div>
            </>
          )}
          {step === "pb" && (
            <>
              <p className="fhelp">
                Por tu respuesta entendemos que por ahora estás conociendo el
                modelo, y está bien: informarse primero es la manera correcta de
                empezar. Abrir una franquicia es una decisión de negocio que
                conviene tomar con calma y con la información completa.
              </p>
              <p className="fmeta" style={{ marginTop: "var(--s5)" }}>
                <span>Sigue conociendo el modelo</span>
              </p>
              <ul className="next">
                {[
                  [
                    "modelo",
                    "En qué se diferencia ser dueño de oficina de ser agente",
                  ],
                  ["respaldo", "Qué respalda a la red y con qué cifras opera"],
                  ["incluye", "Qué recibes exactamente el día que abres"],
                  ["camino", "Los cuatro pasos entre hoy y tu oficina"],
                ].map(([id, text]) => (
                  <li key={id}>
                    <a
                      href={"#" + id}
                      style={{ color: "var(--azul-cl)", fontWeight: 700 }}
                      onClick={() => go("intro")}
                    >
                      {text}
                    </a>
                  </li>
                ))}
              </ul>
              <div className="fnav">{backButton}</div>
            </>
          )}
        </section>
      </form>
    </>
  );
}
function Arrow() {
  return (
    <svg
      className="arrow"
      width="20"
      height="14"
      viewBox="0 0 20 14"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M1 7h17M12 1l6 6-6 6"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="square"
      />
    </svg>
  );
}
